import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, Check, AlertCircle, Loader2,
  Upload, X,
} from "lucide-react";
import api from "@/utils/api";

// ──────────────────────────────────────────────────────────────────────────────
// Constants & validators
// ──────────────────────────────────────────────────────────────────────────────
const STEPS = [
  { id: 1, title: "Basic Details" },
  { id: 2, title: "Business" },
  { id: 3, title: "Identity Docs" },
  { id: 4, title: "Bank Details" },
  { id: 5, title: "Preferences" },
  { id: 6, title: "Rate Agreement" },
];

const toBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.readAsDataURL(file);
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
});

const GSTIN_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[0-9A-Z]{1}Z[0-9A-Z]{1}$/;
const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
const IFSC_REGEX = /^[A-Z]{4}0[A-Z0-9]{6}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_FILE_BYTES = 2 * 1024 * 1024; // 2 MB

const cleanPhone = (val) => {
  let digits = (val || "").replace(/\D/g, "");
  if (digits.length === 12 && digits.startsWith("91")) digits = digits.slice(2);
  if (digits.length === 11 && digits.startsWith("0")) digits = digits.slice(1);
  return digits.slice(0, 10);
};

const initialFormData = {
  // step 1
  full_name: "",
  email: "",
  phone: "",
  password: "Customer@123",
  customer_type: "Individual",
  account_type: "Domestic",
  // step 2
  company_name: "",
  gstin: "",
  pan: "",
  business_address: "",
  // step 3
  aadhaar_number: "",
  aadhaar_front: null,   // { name, size, dataUrl }
  aadhaar_back: null,
  pan_card_image: null,
  // step 4
  account_holder_name: "",
  account_number: "",
  confirm_account_number: "",
  ifsc_code: "",
  bank_name: "",
  bank_account_type: "Savings",
  // step 5
  default_pickup_location_id: "",
  default_pickup_slot: "EVENING",
  credit_wallet_enabled: false,
  credit_limit: "",
};

function validateStep1(f) {
  const e = {};
  if (!f.full_name?.trim()) e.full_name = "Required";
  if (!f.email?.trim()) e.email = "Required";
  else if (!EMAIL_REGEX.test(f.email)) e.email = "Invalid email";
  if (!f.phone || f.phone.length !== 10) e.phone = "10-digit phone required";
  if (!f.password || f.password.length < 6) e.password = "Min 6 characters";
  if (!f.customer_type) e.customer_type = "Required";
  if (!f.account_type) e.account_type = "Required";
  return e;
}

function validateStep2(f) {
  if (f.customer_type === "Individual") return {};
  const e = {};
  if (!f.pan?.trim()) e.pan = "Required";
  else if (!PAN_REGEX.test(f.pan.toUpperCase()))
    e.pan = "Invalid PAN (AAAAA9999A)";
  if (f.gstin && !GSTIN_REGEX.test(f.gstin.toUpperCase()))
    e.gstin = "Invalid GSTIN (15 chars)";
  return e;
}

function validateStep3(f) {
  if (f.customer_type === "Individual") return {};
  const e = {};
  if (!f.aadhaar_number || !/^[0-9]{12}$/.test(f.aadhaar_number))
    e.aadhaar_number = "12-digit Aadhaar required";
  if (!f.aadhaar_front) e.aadhaar_front = "Required";
  if (!f.aadhaar_back) e.aadhaar_back = "Required";
  if (!f.pan_card_image) e.pan_card_image = "Required";
  return e;
}

function validateStep4(f) {
  if (f.customer_type === "Individual") return {};
  const e = {};
  if (!f.account_holder_name?.trim()) e.account_holder_name = "Required";
  if (!f.account_number?.trim()) e.account_number = "Required";
  if (!f.confirm_account_number?.trim()) e.confirm_account_number = "Required";
  else if (f.confirm_account_number !== f.account_number)
    e.confirm_account_number = "Account numbers do not match";
  if (!f.ifsc_code?.trim()) e.ifsc_code = "Required";
  else if (!IFSC_REGEX.test(f.ifsc_code.toUpperCase()))
    e.ifsc_code = "Invalid IFSC (e.g., HDFC0001234)";
  if (!f.bank_name?.trim()) e.bank_name = "Required";
  return e;
}

function validateStep5(f) {
  const e = {};
  if (f.credit_wallet_enabled && (!f.credit_limit || Number(f.credit_limit) <= 0))
    e.credit_limit = "Credit limit must be > 0";
  return e;
}

function validateStep6(f, rateCardFile) {
  const e = {};
  const isIndividual = f.customer_type === "Individual";
  if (!isIndividual && !rateCardFile) {
    e.rate_card_file = "Rate card is required for business customers";
  }
  return e;
}

const VALIDATORS = { 1: validateStep1, 2: validateStep2, 3: validateStep3, 4: validateStep4, 5: validateStep5 };

// ──────────────────────────────────────────────────────────────────────────────
// Shared field helpers
// ──────────────────────────────────────────────────────────────────────────────
const labelCls = "block text-xs font-semibold text-slate-600 mb-1.5";
const inputBase = "w-full px-3 py-2 border rounded-sm text-sm focus:outline-none bg-white";
const inputOk = `${inputBase} border-slate-200 focus:border-brand-500 focus:ring-1 focus:ring-brand-100`;
const inputErr = `${inputBase} border-red-300 focus:border-red-400`;
const errCls = "text-xs text-red-500 mt-1";

function Field({ label, required, error, children, hint }) {
  return (
    <div>
      <label className={labelCls}>
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
      {hint && !error && <p className="text-xs text-slate-400 mt-1">{hint}</p>}
      {error && <p className={errCls}>{error}</p>}
    </div>
  );
}

function FileUpload({ label, required, value, error, onChange, testid }) {
  const ref = useRef(null);
  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/^image\//.test(f.type)) {
      toast.error("Please upload an image file");
      return;
    }
    if (f.size > MAX_FILE_BYTES) {
      toast.error(`${label}: file must be under 2 MB`);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      onChange({ name: f.name, size: f.size, dataUrl: reader.result });
    };
    reader.readAsDataURL(f);
  };

  return (
    <div>
      <label className={labelCls}>
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {value ? (
        <div className="flex items-center gap-3 border border-slate-200 rounded-sm p-2 bg-slate-50">
          <img
            src={value.dataUrl}
            alt={value.name}
            className="w-16 h-16 object-cover rounded-sm border border-slate-200"
          />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-slate-700 truncate">{value.name}</p>
            <p className="text-[11px] text-slate-400">{(value.size / 1024).toFixed(1)} KB</p>
          </div>
          <button
            type="button"
            onClick={() => onChange(null)}
            className="text-slate-400 hover:text-red-500 p-1"
            data-testid={`${testid}-remove`}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => ref.current?.click()}
          className={`w-full flex flex-col items-center justify-center gap-1 px-3 py-5 border-2 border-dashed rounded-sm cursor-pointer transition-colors ${
            error ? "border-red-300 bg-red-50/30" : "border-slate-200 bg-slate-50 hover:border-brand-400 hover:bg-brand-50/30"
          }`}
          data-testid={testid}
        >
          <Upload className="w-5 h-5 text-slate-400" />
          <span className="text-xs font-semibold text-slate-600">Click to upload</span>
          <span className="text-[11px] text-slate-400">JPG / PNG · max 2 MB</span>
        </button>
      )}
      <input
        ref={ref}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onPick}
      />
      {error && <p className={errCls}>{error}</p>}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Step components (inline within file to keep "one new file" rule)
// ──────────────────────────────────────────────────────────────────────────────
function Step1Basic({ formData, setFormData, errors }) {
  const set = (k, v) => setFormData((p) => ({ ...p, [k]: v }));
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="kyc-step-1">
      <Field label="Full Name" required error={errors.full_name}>
        <input
          value={formData.full_name}
          onChange={(e) => set("full_name", e.target.value)}
          placeholder="Customer's full name"
          className={errors.full_name ? inputErr : inputOk}
          data-testid="kyc-full-name"
        />
      </Field>
      <Field label="Email" required error={errors.email}>
        <input
          type="email"
          value={formData.email}
          onChange={(e) => set("email", e.target.value)}
          placeholder="customer@example.com"
          className={errors.email ? inputErr : inputOk}
          data-testid="kyc-email"
        />
      </Field>
      <Field label="Phone" required error={errors.phone}>
        <input
          value={formData.phone}
          onChange={(e) => set("phone", cleanPhone(e.target.value))}
          onPaste={(e) => {
            e.preventDefault();
            set("phone", cleanPhone(e.clipboardData.getData("text")));
          }}
          maxLength={10}
          placeholder="9876543210"
          className={
            errors.phone || (formData.phone && formData.phone.length < 10)
              ? inputErr : inputOk
          }
          data-testid="kyc-phone"
        />
      </Field>
      <Field label="Password" required error={errors.password} hint="Default: Customer@123">
        <input
          type="text"
          value={formData.password}
          onChange={(e) => set("password", e.target.value)}
          className={errors.password ? inputErr : inputOk}
          data-testid="kyc-password"
        />
      </Field>
      <Field label="Customer Type" required error={errors.customer_type}>
        <select
          value={formData.customer_type}
          onChange={(e) => set("customer_type", e.target.value)}
          className={errors.customer_type ? inputErr : inputOk}
          data-testid="kyc-customer-type"
        >
          <option>Individual</option>
          <option>Business</option>
          <option>Enterprise</option>
        </select>
      </Field>
      <Field label="Account Type" required error={errors.account_type}>
        <select
          value={formData.account_type}
          onChange={(e) => set("account_type", e.target.value)}
          className={errors.account_type ? inputErr : inputOk}
          data-testid="kyc-account-type"
        >
          <option>Domestic</option>
          <option>International</option>
        </select>
      </Field>
    </div>
  );
}

function Step2Business({ formData, setFormData, errors }) {
  const set = (k, v) => setFormData((p) => ({ ...p, [k]: v }));
  const isIndividual = formData.customer_type === "Individual";
  return (
    <div className="space-y-4" data-testid="kyc-step-2">
      {isIndividual && (
        <div className="bg-blue-50 border border-blue-200 rounded-sm px-3 py-2 text-xs text-blue-700">
          Business details are optional for Individual customers. PAN is still required.
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Company Name" error={errors.company_name}>
          <input
            value={formData.company_name}
            onChange={(e) => set("company_name", e.target.value)}
            placeholder="Registered business name"
            className={inputOk}
            data-testid="kyc-company-name"
          />
        </Field>
        <Field label="GSTIN" error={errors.gstin} hint="15 alphanumeric chars">
          <input
            value={formData.gstin}
            onChange={(e) => set("gstin", e.target.value.toUpperCase())}
            placeholder="22ABCDE1234F1Z5"
            maxLength={15}
            className={errors.gstin ? inputErr : inputOk}
            data-testid="kyc-gstin"
          />
        </Field>
        <Field label="PAN Number" required={!isIndividual} error={errors.pan} hint="Format: AAAAA9999A">
          <input
            value={formData.pan}
            onChange={(e) => set("pan", e.target.value.toUpperCase())}
            placeholder="ABCDE1234F"
            maxLength={10}
            className={errors.pan ? inputErr : inputOk}
            data-testid="kyc-pan"
          />
        </Field>
        <Field label="Business Address" error={errors.business_address}>
          <textarea
            value={formData.business_address}
            onChange={(e) => set("business_address", e.target.value)}
            rows={3}
            placeholder="Registered business address"
            className={`${inputOk} resize-none`}
            data-testid="kyc-business-address"
          />
        </Field>
      </div>
    </div>
  );
}

function Step3Identity({ formData, setFormData, errors }) {
  const set = (k, v) => setFormData((p) => ({ ...p, [k]: v }));
  const isIndividual = formData.customer_type === "Individual";
  return (
    <div className="space-y-5" data-testid="kyc-step-3">
      <Field label="Aadhaar Number" required={!isIndividual} error={errors.aadhaar_number} hint="12 digits">
        <input
          value={formData.aadhaar_number}
          onChange={(e) => set("aadhaar_number", e.target.value.replace(/\D/g, "").slice(0, 12))}
          maxLength={12}
          placeholder="123412341234"
          className={errors.aadhaar_number ? inputErr : inputOk}
          data-testid="kyc-aadhaar-number"
        />
      </Field>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <FileUpload
          label="Aadhaar Front"
          required={!isIndividual}
          value={formData.aadhaar_front}
          error={errors.aadhaar_front}
          onChange={(v) => set("aadhaar_front", v)}
          testid="kyc-upload-aadhaar-front"
        />
        <FileUpload
          label="Aadhaar Back"
          required={!isIndividual}
          value={formData.aadhaar_back}
          error={errors.aadhaar_back}
          onChange={(v) => set("aadhaar_back", v)}
          testid="kyc-upload-aadhaar-back"
        />
        <FileUpload
          label="PAN Card"
          required={!isIndividual}
          value={formData.pan_card_image}
          error={errors.pan_card_image}
          onChange={(v) => set("pan_card_image", v)}
          testid="kyc-upload-pan-card"
        />
      </div>
    </div>
  );
}

function Step4Bank({ formData, setFormData, errors }) {
  const set = (k, v) => setFormData((p) => ({ ...p, [k]: v }));
  const isIndividual = formData.customer_type === "Individual";
  const req = !isIndividual;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="kyc-step-4">
      <Field label="Account Holder Name" required={req} error={errors.account_holder_name}>
        <input
          value={formData.account_holder_name}
          onChange={(e) => set("account_holder_name", e.target.value)}
          placeholder="As per bank records"
          className={errors.account_holder_name ? inputErr : inputOk}
          data-testid="kyc-account-holder-name"
        />
      </Field>
      <Field label="Bank Name" required={req} error={errors.bank_name}>
        <input
          value={formData.bank_name}
          onChange={(e) => set("bank_name", e.target.value)}
          placeholder="HDFC Bank"
          className={errors.bank_name ? inputErr : inputOk}
          data-testid="kyc-bank-name"
        />
      </Field>
      <Field label="Account Number" required={req} error={errors.account_number}>
        <input
          value={formData.account_number}
          onChange={(e) => set("account_number", e.target.value.replace(/\D/g, "").slice(0, 20))}
          placeholder="0123456789012"
          className={errors.account_number ? inputErr : inputOk}
          data-testid="kyc-account-number"
        />
      </Field>
      <Field label="Confirm Account Number" required={req} error={errors.confirm_account_number}>
        <input
          value={formData.confirm_account_number}
          onChange={(e) => set("confirm_account_number", e.target.value.replace(/\D/g, "").slice(0, 20))}
          placeholder="Re-enter account number"
          className={errors.confirm_account_number ? inputErr : inputOk}
          data-testid="kyc-confirm-account-number"
        />
      </Field>
      <Field label="IFSC Code" required={req} error={errors.ifsc_code} hint="e.g., HDFC0001234">
        <input
          value={formData.ifsc_code}
          onChange={(e) => set("ifsc_code", e.target.value.toUpperCase().slice(0, 11))}
          placeholder="HDFC0001234"
          maxLength={11}
          className={errors.ifsc_code ? inputErr : inputOk}
          data-testid="kyc-ifsc"
        />
      </Field>
      <Field label="Account Type" required={req} error={errors.bank_account_type}>
        <select
          value={formData.bank_account_type}
          onChange={(e) => set("bank_account_type", e.target.value)}
          className={inputOk}
          data-testid="kyc-bank-account-type"
        >
          <option>Savings</option>
          <option>Current</option>
          <option>Overdraft</option>
        </select>
      </Field>
    </div>
  );
}

function Step5Prefs({ formData, setFormData, errors, pickupLocations }) {
  const set = (k, v) => setFormData((p) => ({ ...p, [k]: v }));
  const review = useMemo(() => buildReview(formData), [formData]);

  return (
    <div className="space-y-6" data-testid="kyc-step-5">
      {/* Preferences */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Default Pickup Location" error={errors.default_pickup_location_id}>
          <select
            value={formData.default_pickup_location_id}
            onChange={(e) => set("default_pickup_location_id", e.target.value)}
            className={inputOk}
            data-testid="kyc-default-pickup-location"
          >
            <option value="">— Select pickup location —</option>
            {pickupLocations.map((p) => (
              <option key={p.id} value={p.id}>
                {(p.code || p.name) + " — " + (p.address || p.city || "")}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Default Pickup Slot">
          <div className="grid grid-cols-3 gap-2">
            {[
              ["MIDDAY", "Mid Day", "10:00-14:00"],
              ["EVENING", "Evening", "14:00-18:00"],
              ["LATE_EVENING", "Late Evening", "18:00-21:00"],
            ].map(([val, lbl, time]) => (
              <button
                type="button"
                key={val}
                onClick={() => set("default_pickup_slot", val)}
                data-testid={`kyc-slot-${val}`}
                className={`flex flex-col items-center py-2 px-1 rounded-sm border-2 transition-all text-xs ${
                  formData.default_pickup_slot === val
                    ? "border-brand-500 bg-brand-50 text-brand-700"
                    : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                }`}
              >
                <span className="font-semibold">{lbl}</span>
                <span className="text-slate-400">{time}</span>
              </button>
            ))}
          </div>
        </Field>
        <div>
          <label className={labelCls}>Credit Wallet</label>
          <button
            type="button"
            onClick={() => set("credit_wallet_enabled", !formData.credit_wallet_enabled)}
            data-testid="kyc-credit-wallet-toggle"
            className={`px-4 py-2 rounded-sm text-sm font-semibold border-2 transition-colors w-full text-left ${
              formData.credit_wallet_enabled
                ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                : "border-slate-200 bg-white text-slate-500"
            }`}
          >
            {formData.credit_wallet_enabled ? "Enabled" : "Disabled"}
          </button>
        </div>
        {formData.credit_wallet_enabled && (
          <Field label="Credit Limit (₹)" required error={errors.credit_limit}>
            <input
              type="number"
              min="0"
              value={formData.credit_limit}
              onChange={(e) => set("credit_limit", e.target.value)}
              placeholder="50000"
              className={errors.credit_limit ? inputErr : inputOk}
              data-testid="kyc-credit-limit"
            />
          </Field>
        )}
      </div>

      {/* Review summary */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4" data-testid="kyc-review-card">
        <h3 className="font-semibold text-slate-800 text-sm uppercase tracking-wider mb-3">Review</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
          {review.map(([k, v]) => (
            <div key={k} className="flex items-start gap-2">
              <span className="text-xs uppercase tracking-wider text-slate-500 min-w-[140px]">{k}</span>
              <span className="text-slate-800 font-medium break-words">{v}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function buildReview(f) {
  const mask = (s, keep = 4) =>
    !s ? "—" : s.length <= keep ? s : "•".repeat(Math.max(0, s.length - keep)) + s.slice(-keep);
  const yn = (b) => (b ? "Yes" : "No");
  return [
    ["Full Name", f.full_name || "—"],
    ["Email", f.email || "—"],
    ["Phone", f.phone || "—"],
    ["Customer Type", f.customer_type || "—"],
    ["Account Type", f.account_type || "—"],
    ["Company", f.company_name || "—"],
    ["GSTIN", f.gstin || "—"],
    ["PAN", f.pan || "—"],
    ["Business Address", f.business_address || "—"],
    ["Aadhaar #", mask(f.aadhaar_number, 4)],
    ["Aadhaar Front", f.aadhaar_front ? f.aadhaar_front.name : "—"],
    ["Aadhaar Back", f.aadhaar_back ? f.aadhaar_back.name : "—"],
    ["PAN Card", f.pan_card_image ? f.pan_card_image.name : "—"],
    ["Bank Holder", f.account_holder_name || "—"],
    ["Bank", f.bank_name || "—"],
    ["A/C #", mask(f.account_number, 4)],
    ["IFSC", f.ifsc_code || "—"],
    ["Bank A/C Type", f.bank_account_type || "—"],
    ["Default Slot", f.default_pickup_slot || "—"],
    ["Credit Wallet", yn(f.credit_wallet_enabled)],
    ["Credit Limit", f.credit_wallet_enabled ? `₹${f.credit_limit || 0}` : "—"],
  ];
}

function Step6RateAgreement({ customerType, rateMarkup, setRateMarkup, rateCardFile, setRateCardFile, error }) {
  const isIndividual = customerType === "Individual";
  const ref = useRef(null);
  const handlePickExcel = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const okType = /\.(xlsx|xls)$/i.test(f.name)
      || f.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      || f.type === "application/vnd.ms-excel";
    if (!okType) {
      toast.error("Please upload an .xlsx or .xls file");
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      toast.error("Rate card must be under 5 MB");
      return;
    }
    setRateCardFile(f);
  };

  if (isIndividual) {
    return (
      <div className="space-y-5" data-testid="kyc-step-6">
        <div>
          <h2 className="font-heading text-lg font-bold text-slate-800">Rate Preference (Optional)</h2>
          <p className="text-sm text-slate-500 mt-1">Global rates apply by default. You can add a markup or discount.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Rate Adjustment %">
            <input
              type="number"
              min={-50}
              max={100}
              step="0.5"
              value={rateMarkup}
              onChange={(e) => {
                let v = e.target.value === "" ? 0 : Number(e.target.value);
                if (v > 100) v = 100;
                if (v < -50) v = -50;
                setRateMarkup(v);
              }}
              placeholder="0"
              className={inputOk}
              data-testid="kyc-rate-markup-individual"
            />
            <ul className="text-xs text-slate-400 mt-1.5 space-y-0.5">
              <li>+ value = markup above global rates</li>
              <li>- value = discount below global rates</li>
              <li>Leave 0 to use global rates as-is</li>
            </ul>
          </Field>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-sm px-3 py-2 text-xs text-blue-700">
          This customer can also use discount coupons at time of booking.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="kyc-step-6">
      <div>
        <h2 className="font-heading text-lg font-bold text-slate-800">Rate Agreement (Required)</h2>
        <p className="text-sm text-slate-500 mt-1">Upload the agreed rate card for this business customer.</p>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-sm px-3 py-2 text-xs text-amber-800 font-semibold">
        Rate card upload is mandatory for Business and Enterprise customers.
      </div>

      <div>
        <label className={labelCls}>
          Rate Card Excel<span className="text-red-500 ml-0.5">*</span>
        </label>
        {rateCardFile ? (
          <div className="flex items-center gap-3 border border-slate-200 rounded-sm p-3 bg-emerald-50/40" data-testid="kyc-rate-card-file">
            <div className="w-10 h-10 rounded-sm bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-bold">XLS</div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-800 truncate">{rateCardFile.name}</p>
              <p className="text-[11px] text-slate-500">{(rateCardFile.size / 1024).toFixed(1)} KB</p>
            </div>
            <button
              type="button"
              onClick={() => setRateCardFile(null)}
              className="text-slate-400 hover:text-red-500 p-1"
              data-testid="kyc-rate-card-remove"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => ref.current?.click()}
            className={`w-full flex flex-col items-center justify-center gap-1 px-3 py-6 border-2 border-dashed rounded-sm cursor-pointer transition-colors ${
              error ? "border-red-300 bg-red-50/30" : "border-slate-200 bg-slate-50 hover:border-emerald-400 hover:bg-emerald-50/30"
            }`}
            data-testid="kyc-rate-card-upload"
          >
            <div className="w-10 h-10 rounded-sm bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-bold">XLS</div>
            <span className="text-xs font-semibold text-slate-600">Click to upload rate card</span>
            <span className="text-[11px] text-slate-400">.xlsx or .xls · max 5 MB</span>
          </button>
        )}
        <input
          ref={ref}
          type="file"
          accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
          className="hidden"
          onChange={handlePickExcel}
        />
        <p className="text-xs text-slate-400 mt-1.5">
          Download the rate card template from Rate Engine → Download Rate Card
        </p>
        {error && <p className={errCls} data-testid="kyc-rate-card-error">{error}</p>}
      </div>

      <div className="pt-2 border-t border-slate-100">
        <Field label="Additional Markup % (optional)" hint="Applied on top of uploaded rates">
          <input
            type="number"
            min={0}
            max={100}
            step="0.5"
            value={rateMarkup}
            onChange={(e) => {
              let v = e.target.value === "" ? 0 : Number(e.target.value);
              if (v > 100) v = 100;
              if (v < 0) v = 0;
              setRateMarkup(v);
            }}
            placeholder="0"
            className={inputOk}
            data-testid="kyc-rate-markup-business"
          />
        </Field>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Page
// ──────────────────────────────────────────────────────────────────────────────
export default function CustomerKYC() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState(initialFormData);
  const [errorsByStep, setErrorsByStep] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState("");
  const [pickupLocations, setPickupLocations] = useState([]);
  const [rateCardFile, setRateCardFile] = useState(null);
  const [rateMarkup, setRateMarkup] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/v2/pickup-locations");
        const list = Array.isArray(r.data) ? r.data : (r.data?.items || r.data?.results || []);
        setPickupLocations(list);
      } catch (_) {
        // silent — preferences step will show empty dropdown
      }
    })();
  }, []);

  const errorCounts = useMemo(() => {
    const counts = {};
    Object.entries(VALIDATORS).forEach(([step, fn]) => {
      counts[step] = Object.keys(fn(formData)).length;
    });
    counts[6] = Object.keys(validateStep6(formData, rateCardFile)).length;
    return counts;
  }, [formData, rateCardFile]);

  const handleNext = () => {
    if (currentStep === 6) {
      const errs = validateStep6(formData, rateCardFile);
      if (Object.keys(errs).length > 0) {
        setErrorsByStep((p) => ({ ...p, 6: errs }));
        toast.error(errs.rate_card_file || "Please complete this step");
        return;
      }
      setErrorsByStep((p) => ({ ...p, 6: {} }));
      return;
    }
    const errs = VALIDATORS[currentStep](formData);
    if (Object.keys(errs).length > 0) {
      setErrorsByStep((p) => ({ ...p, [currentStep]: errs }));
      toast.error(`Please fix ${Object.keys(errs).length} error(s) to continue`);
      return;
    }
    setErrorsByStep((p) => ({ ...p, [currentStep]: {} }));
    if (currentStep < STEPS.length) setCurrentStep((s) => s + 1);
  };

  // Clear all fields belonging to a given (optional) step — used by "Skip" for Individuals on steps 2/3/4.
  const clearStepData = (step) => {
    const reset = (keys) => setFormData((p) => {
      const next = { ...p };
      keys.forEach((k) => { next[k] = initialFormData[k]; });
      return next;
    });
    if (step === 2) reset(["company_name", "gstin", "pan", "business_address"]);
    if (step === 3) reset(["aadhaar_number", "aadhaar_front", "aadhaar_back", "pan_card_image"]);
    if (step === 4) reset([
      "account_holder_name", "account_number", "confirm_account_number",
      "ifsc_code", "bank_name", "bank_account_type",
    ]);
  };

  const handleSkipStep = () => {
    clearStepData(currentStep);
    setErrorsByStep((p) => ({ ...p, [currentStep]: {} }));
    if (currentStep < STEPS.length) setCurrentStep((s) => s + 1);
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep((s) => s - 1);
  };

  const handleSubmit = async ({ skipMarkup = false } = {}) => {
    // Validate all steps (1-5)
    const all = {};
    let firstError = null;
    for (const [step, fn] of Object.entries(VALIDATORS)) {
      const errs = fn(formData);
      if (Object.keys(errs).length) {
        all[step] = errs;
        if (firstError === null) firstError = Number(step);
      }
    }
    // Step 6 validation (rate agreement)
    const step6Errs = validateStep6(formData, rateCardFile);
    if (Object.keys(step6Errs).length) {
      all[6] = step6Errs;
      if (firstError === null) firstError = 6;
    }
    setErrorsByStep(all);
    if (firstError) {
      setCurrentStep(firstError);
      toast.error(`Errors found in step ${firstError}. Please review highlighted fields.`);
      return;
    }

    setSubmitting(true);
    setApiError("");
    try {
      const isIndividual = formData.customer_type === "Individual";
      const effectiveMarkup = skipMarkup ? 0 : Number(rateMarkup || 0);
      const rateCardData = rateCardFile ? await toBase64(rateCardFile) : null;

      // Path B: POST /api/v2/customers/create (mapped field names).
      const isIndividualLocal = formData.customer_type === "Individual";
      const bankPayload =
        formData.account_holder_name || formData.account_number || formData.ifsc_code || formData.bank_name
          ? {
              account_holder_name: formData.account_holder_name || null,
              account_number: formData.account_number || null,
              ifsc_code: formData.ifsc_code || null,
              bank_name: formData.bank_name || null,
              account_type: formData.bank_account_type || null,
            }
          : null;
      const v2Payload = {
        customer_type: (formData.customer_type || "Individual").toLowerCase() === "enterprise" ? "enterprise" : "retail",
        company_name: formData.company_name || null,
        contact_person: formData.full_name,
        mobile: formData.phone,
        email: formData.email,
        password: formData.password,
        business_type: formData.customer_type || null,
        gst_number: formData.gstin || null,
        pan_number: formData.pan || null,
        account_type: formData.account_type || null,
        business_address: formData.business_address || null,
        aadhaar_number: formData.aadhaar_number || null,
        aadhaar_front_url: formData.aadhaar_front?.dataUrl || null,
        aadhaar_back_url: formData.aadhaar_back?.dataUrl || null,
        pan_card_url: formData.pan_card_image?.dataUrl || null,
        bank: bankPayload,
        rate_agreement: {
          type: isIndividualLocal ? "MARKUP" : "CUSTOM_RATE_CARD",
          markup_percent: effectiveMarkup,
          rate_card_filename: rateCardFile?.name || null,
          rate_card_data: rateCardData,
          status: "PENDING_APPROVAL",
        },
        default_pickup_slot: formData.default_pickup_slot,
        default_pickup_location_id: formData.default_pickup_location_id || null,
        credit_wallet_enabled: !!formData.credit_wallet_enabled,
        credit_limit: formData.credit_wallet_enabled ? Number(formData.credit_limit || 0) : 0,
        status: "PENDING_APPROVAL",
      };

      await api.post("/v2/customers/create", v2Payload);
      toast.success("Customer created and sent for approval. Rate agreement pending admin review.");
      setTimeout(() => navigate("/admin/customers"), 800);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.response?.data?.message || e.message || "Network error";
      setApiError(typeof msg === "string" ? msg : JSON.stringify(msg));
      toast.error("Failed to create customer");
    } finally {
      setSubmitting(false);
    }
  };

  const stepProps = {
    formData,
    setFormData,
    errors: errorsByStep[currentStep] || {},
  };

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="customer-kyc-page">
        {/* Header */}
        <div>
          <button
            onClick={() => navigate("/admin/customers")}
            className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-1.5 transition"
            data-testid="back-to-customers-btn"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Customers
          </button>
          <div className="flex items-end justify-between flex-wrap gap-3">
            <div>
              <h1 className="text-2xl font-bold text-slate-800" data-testid="customer-kyc-title">Add New Customer</h1>
              <p className="text-sm text-slate-500">Complete all 6 steps to submit for approval</p>
            </div>
            <div className="text-sm text-slate-500" data-testid="kyc-step-counter">
              Step <span className="font-bold text-slate-800">{currentStep}</span> of {STEPS.length}
            </div>
          </div>
          {/* Progress bar */}
          <div className="mt-3 h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-emerald-500 transition-all"
              style={{ width: `${(currentStep / STEPS.length) * 100}%` }}
              data-testid="kyc-progress-bar"
            />
          </div>
        </div>

        {/* Step Indicator */}
        <StepIndicator
          steps={STEPS}
          currentStep={currentStep}
          errorCounts={errorCounts}
          onJump={(id) => setCurrentStep(id)}
          customerType={formData.customer_type}
        />

        {/* Content card */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 md:p-7">
          {apiError && currentStep === 6 && (
            <div
              className="mb-5 flex items-start gap-2 bg-red-50 border border-red-200 rounded-md px-3 py-2 text-sm text-red-700"
              data-testid="kyc-api-error"
            >
              <AlertCircle className="w-4 h-4 mt-0.5" />
              <span>{apiError}</span>
            </div>
          )}
          {currentStep === 1 && <Step1Basic {...stepProps} />}
          {currentStep === 2 && <Step2Business {...stepProps} />}
          {currentStep === 3 && <Step3Identity {...stepProps} />}
          {currentStep === 4 && <Step4Bank {...stepProps} />}
          {currentStep === 5 && <Step5Prefs {...stepProps} pickupLocations={pickupLocations} />}
          {currentStep === 6 && (
            <Step6RateAgreement
              customerType={formData.customer_type}
              rateMarkup={rateMarkup}
              setRateMarkup={setRateMarkup}
              rateCardFile={rateCardFile}
              setRateCardFile={setRateCardFile}
              error={errorsByStep[6]?.rate_card_file}
            />
          )}
        </div>

        {/* Bottom Navigation */}
        <div
          className="sticky bottom-0 bg-white border border-slate-200 rounded-xl shadow-lg px-4 py-3 flex items-center justify-between"
          data-testid="kyc-step-nav"
        >
          <div className="w-32">
            {currentStep > 1 && (
              <Button
                variant="ghost"
                onClick={handleBack}
                disabled={submitting}
                data-testid="kyc-back-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </Button>
            )}
          </div>
          <div className="text-sm text-slate-500">
            Step {currentStep} of {STEPS.length}
          </div>
          <div className="w-60 flex justify-end gap-2">
            {currentStep < STEPS.length ? (
              formData.customer_type === "Individual" && [2, 3, 4].includes(currentStep) ? (
                <>
                  <Button
                    variant="ghost"
                    onClick={handleSkipStep}
                    disabled={submitting}
                    data-testid="kyc-skip-step-btn"
                    className="text-slate-600"
                  >
                    Skip
                  </Button>
                  <Button
                    onClick={handleNext}
                    className="bg-slate-800 hover:bg-slate-900 text-white"
                    disabled={submitting}
                    data-testid="kyc-next-btn"
                  >
                    Save &amp; Next <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </>
              ) : (
                <Button
                  onClick={handleNext}
                  className="bg-slate-800 hover:bg-slate-900 text-white"
                  disabled={submitting}
                  data-testid="kyc-next-btn"
                >
                  Next <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              )
            ) : (
              <>
                {formData.customer_type === "Individual" && (
                  <Button
                    variant="ghost"
                    onClick={() => handleSubmit({ skipMarkup: true })}
                    disabled={submitting}
                    data-testid="kyc-skip-submit-btn"
                    className="text-slate-600"
                  >
                    Skip &amp; Submit
                  </Button>
                )}
                <Button
                  onClick={() => handleSubmit()}
                  disabled={
                    submitting ||
                    (formData.customer_type !== "Individual" && !rateCardFile)
                  }
                  className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-60"
                  data-testid="kyc-submit-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-1" /> Create Customer &amp; Send for Approval
                    </>
                  )}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

function StepIndicator({ steps, currentStep, errorCounts, onJump, customerType }) {
  const isIndividual = customerType === "Individual";
  const optionalTitle = (id) => {
    if (!isIndividual) return null;
    if (id === 2) return "Business (Optional)";
    if (id === 3) return "Documents (Optional)";
    if (id === 4) return "Bank (Optional)";
    return null;
  };
  return (
    <div
      className="bg-white border border-slate-200 rounded-xl p-3 md:p-4"
      data-testid="kyc-step-indicator"
    >
      <div className="flex items-center">
        {steps.map((step, idx) => {
          const isActive = step.id === currentStep;
          const isCompleted = step.id < currentStep;
          const errCount = errorCounts[step.id] || 0;
          return (
            <div key={step.id} className="flex items-center flex-1 last:flex-none">
              <button
                type="button"
                onClick={() => onJump(step.id)}
                className="flex items-center gap-2 group"
                data-testid={`kyc-step-${step.id}-button`}
              >
                <div className="relative">
                  <div
                    className={`w-8 h-8 md:w-9 md:h-9 rounded-full flex items-center justify-center text-xs font-bold transition ${
                      isActive
                        ? "bg-slate-800 text-white ring-4 ring-slate-100"
                        : isCompleted
                        ? "bg-emerald-600 text-white"
                        : "bg-slate-100 text-slate-400 border border-slate-200"
                    }`}
                  >
                    {isCompleted ? <Check className="w-4 h-4" /> : <span>{step.id}</span>}
                  </div>
                  {errCount > 0 && step.id !== currentStep && (
                    <span
                      className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center"
                      data-testid={`kyc-step-${step.id}-error-badge`}
                    >
                      {errCount}
                    </span>
                  )}
                </div>
                <span
                  className={`hidden md:block text-xs font-semibold ${
                    isActive ? "text-slate-800" : isCompleted ? "text-emerald-700" : "text-slate-400"
                  }`}
                >
                  {optionalTitle(step.id) || step.title}
                </span>
              </button>
              {idx < steps.length - 1 && (
                <div
                  className={`flex-1 h-0.5 mx-2 md:mx-3 ${
                    step.id < currentStep ? "bg-emerald-500" : "bg-slate-200"
                  }`}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
